import { useEffect, useState } from "react";
import {
  Users,
  MessagesSquare,
  MessageCircle,
  Flame,
  AlertCircle,
  ChevronRight,
  GraduationCap,
  FileSpreadsheet,
  PlayCircle,
  ChevronDown,
} from "lucide-react";
import { ApiService } from "../../../../services/api";
import Modal from "../../../../components/ui/Modal";
import type { AIObsOverview, AIObsStudentDetail, AIObsStudent } from "../../../../types/ai-observation";

const dateFmt = new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const fmt = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return dateFmt.format(new Date(iso));
  } catch {
    return "—";
  }
};

const TOOL_LABEL: Record<string, string> = {
  open_practice_test: "test açtı",
  open_lesson_video: "video açtı",
  show_pdf_on_canvas: "pdf açtı",
  move_to_next_gain: "sonraki kazanım",
  open_simulation: "simülasyon",
};

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: number | string; color: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
      <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

// ── Öğrenci detay modalı: sohbet transkriptleri + aktivite ──
function StudentDetailModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<AIObsStudentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openConv, setOpenConv] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await ApiService.getAIObservationStudentDetail(userId);
        if (alive) {
          setDetail(d);
          setOpenConv(d.conversations[0]?.conversationId || null);
        }
      } catch (e: any) {
        if (alive) setError(e?.message || "Detay yüklenemedi");
      }
    })();
    return () => {
      alive = false;
    };
  }, [userId]);

  return (
    <Modal isOpen onClose={onClose} title={detail ? `Gözlem · ${detail.user.name}` : "Öğrenci gözlemi"} size="xl" closeOnBackdrop={false}>
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" /> {error}
        </div>
      )}
      {!detail && !error && (
        <div className="grid place-items-center py-14">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      )}
      {detail && (
        <div className="space-y-5">
          {/* Özet */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard icon={MessagesSquare} label="Sohbet" value={detail.summary.conversations} color="bg-blue-100 text-blue-600" />
            <StatCard icon={MessageCircle} label="Toplam tur" value={detail.summary.totalTurns} color="bg-indigo-100 text-indigo-600" />
            <StatCard icon={FileSpreadsheet} label="Test açtı" value={detail.summary.testsOpened} color="bg-emerald-100 text-emerald-600" />
            <StatCard icon={PlayCircle} label="Kazanım geçişi" value={detail.summary.movedNext} color="bg-amber-100 text-amber-600" />
          </div>
          <p className="text-xs text-gray-500">
            İlk: {fmt(detail.summary.firstActivity)} · Son: {fmt(detail.summary.lastActivity)}
            {detail.summary.topics.length ? ` · Konular: ${detail.summary.topics.slice(0, 6).join(", ")}` : ""}
          </p>

          {/* Sohbet transkriptleri */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-900">AI öğretmen sohbetleri</h3>
            {detail.conversations.length === 0 ? (
              <p className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">Henüz kayıtlı sohbet yok.</p>
            ) : (
              <div className="space-y-2">
                {detail.conversations.map((conv) => {
                  const open = openConv === conv.conversationId;
                  return (
                    <div key={conv.conversationId} className="overflow-hidden rounded-lg border border-gray-200">
                      <button
                        onClick={() => setOpenConv(open ? null : conv.conversationId)}
                        className="flex w-full items-center gap-2 bg-gray-50 px-3 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        <MessagesSquare className="h-4 w-4 shrink-0 text-blue-600" />
                        <span className="min-w-0 flex-1 truncate font-medium text-gray-800">
                          {conv.topicTitle || conv.lessonTitle || "Sohbet"}
                        </span>
                        <span className="shrink-0 text-[11px] text-gray-400">{conv.turns.length} tur · {fmt(conv.startedAt)}</span>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && (
                        <div className="max-h-96 space-y-2 overflow-y-auto bg-white p-3">
                          {conv.turns.map((turn, i) => (
                            <div key={i} className="space-y-1.5">
                              {turn.userMessage && (
                                <div className="flex justify-end">
                                  <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-blue-600 px-3 py-1.5 text-sm text-white">
                                    {turn.userMessage}
                                  </div>
                                </div>
                              )}
                              {turn.assistantMessage && (
                                <div className="flex justify-start">
                                  <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-1.5 text-sm text-gray-800">
                                    {turn.assistantMessage}
                                    {turn.toolCalls.length > 0 && (
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {turn.toolCalls.map((t, j) => (
                                          <span key={j} className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                                            {TOOL_LABEL[t] || t}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Aktivite */}
          {detail.activity.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Aktivite (neler yaptı)</h3>
              <ol className="relative space-y-2 border-l border-gray-200 pl-5">
                {detail.activity.map((ev, i) => (
                  <li key={i} className="relative">
                    <span className={`absolute -left-[27px] grid h-6 w-6 place-items-center rounded-full ring-4 ring-white ${ev.type === "taught" ? "bg-blue-100 text-blue-600" : "bg-emerald-100 text-emerald-600"}`}>
                      {ev.type === "taught" ? <GraduationCap className="h-3.5 w-3.5" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                    </span>
                    <div className="rounded-lg border border-gray-200 bg-white p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900">{ev.title}</p>
                        <span className="shrink-0 text-[11px] text-gray-400">{fmt(ev.at)}</span>
                      </div>
                      {ev.detail && <p className="mt-0.5 text-xs text-gray-500">{ev.detail}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function AIObservationTab() {
  const [overview, setOverview] = useState<AIObsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [selected, setSelected] = useState<AIObsStudent | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const o = await ApiService.getAIObservationOverview(days);
        if (alive) setOverview(o);
      } catch (e: any) {
        if (alive) setError(e?.message || "Rapor yüklenemedi");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [days]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto pb-6">
      {/* Başlık + dönem */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">AI Öğretmen Gözlem</h2>
          <p className="text-sm text-gray-400">öğrenciler AI ile ne konuştu, nerede takıldı</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-0.5">
          {[7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${days === d ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
            >
              Son {d} gün
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" /> {error}
        </div>
      )}

      {loading && !overview && (
        <div className="grid place-items-center py-16">
          <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      )}

      {overview && (
        <div className="space-y-5">
          {/* Özet kartları */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard icon={Users} label="Aktif öğrenci" value={overview.activeStudents} color="bg-blue-100 text-blue-600" />
            <StatCard icon={MessagesSquare} label="Sohbet" value={overview.totalConversations} color="bg-indigo-100 text-indigo-600" />
            <StatCard icon={MessageCircle} label="Toplam tur" value={overview.totalTurns} color="bg-emerald-100 text-emerald-600" />
          </div>

          {overview.activeStudents === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
              <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-lg bg-blue-50 text-blue-600">
                <MessagesSquare className="h-6 w-6" />
              </div>
              <p className="mb-1 text-base font-semibold text-gray-900">Bu dönemde AI öğretmen kullanımı yok</p>
              <p className="text-sm text-gray-600">Öğrenciler AI öğretmenle çalışmaya başlayınca konuşmalar burada görünecek.</p>
            </div>
          ) : (
            <>
              {/* Takılma sıcak noktaları */}
              {overview.hotspots.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-1.5">
                    <Flame className="h-4 w-4 text-rose-500" />
                    <h3 className="text-sm font-semibold text-gray-900">Takılma sıcak noktaları</h3>
                    <span className="text-xs text-gray-400">en çok konuşulan konular</span>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {overview.hotspots.slice(0, 6).map((h) => (
                      <div key={h.topic} className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="mb-1 truncate text-sm font-medium text-gray-900" title={h.topic}>{h.topic}</p>
                        <div className="flex items-center gap-3 text-[11px] text-gray-500">
                          <span>{h.students} öğrenci</span>
                          <span>·</span>
                          <span>{h.turns} tur</span>
                          {h.testsOpened > 0 && <><span>·</span><span>{h.testsOpened} test</span></>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Öğrenci listesi */}
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Öğrenciler</h3>
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-4 py-2 font-medium">Öğrenci</th>
                        <th className="px-3 py-2 text-center font-medium">Sohbet</th>
                        <th className="px-3 py-2 text-center font-medium">Tur</th>
                        <th className="px-3 py-2 text-center font-medium">Test</th>
                        <th className="hidden px-3 py-2 font-medium sm:table-cell">Son aktivite</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {overview.students.map((s) => (
                        <tr
                          key={s.userId}
                          onClick={() => setSelected(s)}
                          className="cursor-pointer transition-colors hover:bg-blue-50/50"
                        >
                          <td className="px-4 py-2.5 font-medium text-gray-900">{s.name}</td>
                          <td className="px-3 py-2.5 text-center text-gray-600">{s.conversations}</td>
                          <td className="px-3 py-2.5 text-center text-gray-600">{s.turns}</td>
                          <td className="px-3 py-2.5 text-center text-gray-600">{s.testsOpened}</td>
                          <td className="hidden px-3 py-2.5 text-xs text-gray-400 sm:table-cell">{fmt(s.lastActivity)}</td>
                          <td className="px-3 py-2.5 text-right">
                            <ChevronRight className="ml-auto h-4 w-4 text-gray-300" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {selected && <StudentDetailModal userId={selected.userId} onClose={() => setSelected(null)} />}
    </div>
  );
}
